/* ============================================================
   Archvela — Interactions v2
   - Hero sequence (is-ready class)
   - Sticky header blur on scroll
   - Bidirectional scroll reveals (auto-tag .av-anim + stagger)
   - Count-up stats
   - 3D tilt on bento cards
   - FAQ accordion (one-open rule)
   - Thumbnail switcher + swipe (mobile)
   - Thumbs column auto-height sync with main image
   - Quantity controls
   - Add to cart (Ajax) -> direct checkout redirect
   - Sticky mobile buy bar visibility
   - bfcache button reset
   ============================================================ */
(function () {
  "use strict";

  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  ready(function () {
    const page = document.querySelector(".av-page");
    if (!page) return;

    requestAnimationFrame(() => page.classList.add("is-ready"));

    initHero();
    initStickyHeader();
    initReveals();
    initCounters();
    initBentoTilt();
    initFAQ();
    initQuantity();
    initAddToCart();
    initThumbs();
    initThumbSwipe();
    initThumbHeightSync();
    initStickyBuyBar();
    injectSpinnerKeyframes();
    initPageShowReset();
    initCartForm();
  });

  /* ----- Cart page: AJAX qty / remove / subtotal refresh ----- */
  function initCartForm() {
    const cartForm = document.querySelector("[data-av-cart-form]");
    if (!cartForm) return;

    const formatMoney = (cents) => {
      if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents);
      const amount = (cents / 100).toFixed(2);
      return amount;
    };

    const setLoading = (item, loading) => {
      if (!item) return;
      item.classList.toggle("is-removing", !!loading);
    };

    const refreshSummary = (cart) => {
      document.querySelectorAll("[data-av-subtotal]").forEach((el) => {
        el.textContent = formatMoney(cart.total_price);
      });
      document.querySelectorAll("[data-av-checkout-price]").forEach((el) => {
        el.textContent = formatMoney(cart.total_price);
      });
      cart.items.forEach((it) => {
        const line = document.querySelector(`[data-av-cart-item][data-key="${CSS.escape(it.key)}"]`);
        if (!line) return;
        const priceEl = line.querySelector("[data-av-line-price]");
        if (priceEl) priceEl.textContent = formatMoney(it.final_line_price);
      });
    };

    const changeQuantity = async (key, quantity, item) => {
      setLoading(item, true);
      try {
        const res = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: key, quantity: quantity }),
        });
        const cart = await res.json();
        if (quantity === 0 && item) {
          item.style.transition = "opacity 0.25s, transform 0.25s, height 0.25s, margin 0.25s, padding 0.25s";
          item.style.opacity = "0";
          item.style.transform = "translateX(-12px)";
          setTimeout(() => {
            item.remove();
            if (!document.querySelector("[data-av-cart-item]")) window.location.reload();
          }, 260);
        } else {
          setLoading(item, false);
        }
        refreshSummary(cart);
      } catch (err) {
        setLoading(item, false);
      }
    };

    cartForm.querySelectorAll("[data-av-qty-minus], [data-av-qty-plus]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const key = btn.dataset.key;
        const input = cartForm.querySelector(`[data-av-qty-input][data-key="${CSS.escape(key)}"]`);
        if (!input) return;
        const current = parseInt(input.value, 10) || 0;
        const next = btn.hasAttribute("data-av-qty-minus") ? Math.max(0, current - 1) : current + 1;
        input.value = next;
        const item = btn.closest("[data-av-cart-item]");
        changeQuantity(key, next, item);
      });
    });

    cartForm.querySelectorAll("[data-av-qty-input]").forEach((input) => {
      let timer;
      input.addEventListener("change", () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          const key = input.dataset.key;
          const qty = Math.max(0, parseInt(input.value, 10) || 0);
          const item = input.closest("[data-av-cart-item]");
          changeQuantity(key, qty, item);
        }, 250);
      });
    });

    cartForm.querySelectorAll("[data-av-cart-remove]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const key = btn.dataset.key;
        const item = btn.closest("[data-av-cart-item]");
        changeQuantity(key, 0, item);
      });
    });

    const checkoutBtn = cartForm.querySelector("[data-av-checkout-btn]");
    if (checkoutBtn) {
      const originalHTML = checkoutBtn.innerHTML;
      checkoutBtn.dataset.originalText = originalHTML;
      cartForm.addEventListener("submit", () => {
        checkoutBtn.classList.add("is-loading");
        checkoutBtn.innerHTML = '<span style="display:inline-block;width:18px;height:18px;border:2px solid rgba(22,24,28,0.3);border-top-color:#16181C;border-radius:50%;animation:av-spin 0.7s linear infinite"></span>';
      });
      window.addEventListener("pageshow", () => {
        if (checkoutBtn.classList.contains("is-loading")) {
          checkoutBtn.classList.remove("is-loading");
          checkoutBtn.innerHTML = checkoutBtn.dataset.originalText || originalHTML;
        }
      });
    }
  }

  /* ----- Hero word-reveal + background slideshow ----- */
  function initHero() {
    const hero = document.querySelector(".av-hero");
    if (!hero) return;
    requestAnimationFrame(() => hero.classList.add("is-loaded"));

    const slides = hero.querySelectorAll("[data-av-slide]");
    if (slides.length <= 1 || reduced) return;
    const cfg = parseInt(hero.dataset.slideInterval, 10);
    const intervalMs = Math.max(1500, (Number.isFinite(cfg) ? cfg : 3) * 1000);
    let idx = 0;
    setInterval(() => {
      slides[idx].classList.remove("is-active");
      idx = (idx + 1) % slides.length;
      slides[idx].classList.add("is-active");
    }, intervalMs);
  }

  /* ----- Sticky header ----- */
  function initStickyHeader() {
    const header = document.querySelector(".av-header");
    if (!header) return;
    const threshold = 40;
    const onScroll = () => {
      if (window.scrollY > threshold) header.classList.add("is-scrolled");
      else header.classList.remove("is-scrolled");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  /* ----- Scroll reveals (bidirectional) ----- */
  function initReveals() {
    const selectors = [
      ".av-stat",
      ".av-bento__card",
      ".av-pressure",
      ".av-pressure__title",
      ".av-pressure__grid > *",
      ".av-usecase",
      ".av-usecases__head > *",
      ".av-features__head > *",
      ".av-lifestyle",
      ".av-lifestyle__title",
      ".av-buy__gallery",
      ".av-buy__info > *",
      ".av-faq__title",
      ".av-faq__item",
      ".av-final__title",
      ".av-final__cta",
      ".av-final__media",
    ];
    const nodes = document.querySelectorAll(selectors.join(","));

    nodes.forEach((el) => {
      el.classList.add("av-anim");
      const parent = el.parentElement;
      if (parent) {
        const idx = Array.prototype.indexOf.call(parent.children, el);
        el.style.setProperty("--av-i", Math.max(0, idx));
      }
    });

    if (!("IntersectionObserver" in window) || reduced) {
      nodes.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-visible");
          else entry.target.classList.remove("is-visible");
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    nodes.forEach((el) => io.observe(el));
  }

  /* ----- Count-up stats (once) ----- */
  function initCounters() {
    const nums = document.querySelectorAll("[data-count-to]");
    if (!("IntersectionObserver" in window) || reduced) {
      nums.forEach((n) => (n.textContent = n.dataset.countTo));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          runCounter(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.5 }
    );
    nums.forEach((n) => io.observe(n));
  }
  function runCounter(el) {
    const target = parseInt(el.dataset.countTo, 10);
    if (!Number.isFinite(target)) return;
    const duration = 1400;
    const start = performance.now();
    function tick(now) {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = Math.round(target * eased).toString();
      if (t < 1) requestAnimationFrame(tick);
      else el.textContent = target.toString();
    }
    requestAnimationFrame(tick);
  }

  /* ----- 3D tilt on bento cards ----- */
  function initBentoTilt() {
    if (reduced) return;
    document.querySelectorAll(".av-bento__card").forEach((card) => {
      card.addEventListener("mousemove", (e) => {
        const r = card.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        card.style.transform = `perspective(900px) rotateY(${x * 4}deg) rotateX(${-y * 4}deg) translateY(-2px)`;
      });
      card.addEventListener("mouseleave", () => {
        card.style.transform = "";
      });
    });
  }

  /* ----- FAQ accordion (single-open) ----- */
  function initFAQ() {
    document.querySelectorAll(".av-faq__item").forEach((item) => {
      const btn = item.querySelector(".av-faq__q");
      if (!btn) return;
      btn.addEventListener("click", () => {
        const open = item.classList.contains("is-open");
        document
          .querySelectorAll(".av-faq__item.is-open")
          .forEach((o) => o.classList.remove("is-open"));
        if (!open) item.classList.add("is-open");
      });
    });
  }

  /* ----- Quantity controls ----- */
  function initQuantity() {
    document.querySelectorAll(".av-buy__qty").forEach((wrap) => {
      const input = wrap.querySelector("input");
      const minus = wrap.querySelector("[data-qty-minus]");
      const plus = wrap.querySelector("[data-qty-plus]");
      if (!input) return;
      minus && minus.addEventListener("click", () => {
        const v = Math.max(1, parseInt(input.value || "1", 10) - 1);
        input.value = v;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
      plus && plus.addEventListener("click", () => {
        const v = parseInt(input.value || "1", 10) + 1;
        input.value = v;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });
  }

  /* ----- Add to cart -> direct checkout ----- */
  function initAddToCart() {
    document.querySelectorAll("[data-av-add-btn]").forEach((btn) => {
      if (!btn.dataset.originalText) btn.dataset.originalText = btn.innerHTML;
    });
    document.querySelectorAll("[data-av-cart]").forEach((form) => {
      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        const btn = form.querySelector("[data-av-add-btn]");
        if (!btn) return;
        const original = btn.dataset.originalText || btn.innerHTML;
        btn.dataset.originalText = original;
        btn.classList.add("is-loading");
        btn.innerHTML =
          '<span style="display:inline-block;width:16px;height:16px;border:2px solid rgba(22,24,28,0.3);border-top-color:#16181C;border-radius:50%;animation:av-spin 0.7s linear infinite"></span>';
        try {
          const data = new FormData(form);
          const res = await fetch("/cart/add.js", {
            method: "POST",
            body: data,
            headers: { Accept: "application/json" },
          });
          if (!res.ok) throw new Error("Add failed");
          btn.innerHTML = "✓ Redirecting…";
          const root =
            (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
          window.location.href = root + "checkout";
        } catch (err) {
          btn.innerHTML = "Try again";
          setTimeout(() => {
            btn.innerHTML = original;
            btn.classList.remove("is-loading");
          }, 1600);
        }
      });
    });
  }

  /* ----- Thumbnail switcher ----- */
  function setActiveThumb(thumb, opts) {
    if (!thumb) return;
    const gallery = thumb.closest(".av-buy__gallery");
    if (!gallery) return;
    gallery.querySelectorAll("[data-av-thumb]").forEach((t) => t.classList.remove("is-active"));
    thumb.classList.add("is-active");
    const main = gallery.querySelector("[data-av-main-image]");
    if (main && thumb.dataset.avThumb) main.src = thumb.dataset.avThumb;
    if (!opts || opts.scroll !== false) {
      const list = thumb.parentElement;
      if (list) {
        const t = thumb.getBoundingClientRect();
        const l = list.getBoundingClientRect();
        if (t.top < l.top || t.bottom > l.bottom || t.left < l.left || t.right > l.right) {
          thumb.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
        }
      }
    }
  }
  function initThumbs() {
    document.querySelectorAll("[data-av-thumb]").forEach((thumb) => {
      thumb.addEventListener("click", () => setActiveThumb(thumb));
    });
  }

  /* ----- Thumbs column height sync with main image ----- */
  function initThumbHeightSync() {
    document.querySelectorAll(".av-buy__gallery").forEach((gallery) => {
      const main = gallery.querySelector(".av-buy__main");
      const thumbs = gallery.querySelector("[data-av-thumbs-wrap]");
      if (!main || !thumbs) return;
      const sync = () => {
        const h = main.getBoundingClientRect().height;
        if (h > 0) thumbs.style.setProperty("--av-thumb-max-h", h + "px");
      };
      sync();
      if ("ResizeObserver" in window) new ResizeObserver(sync).observe(main);
      else window.addEventListener("resize", sync);
      const img = main.querySelector("img");
      if (img) img.addEventListener("load", sync);
    });
  }

  /* ----- Swipe on main image (mobile) ----- */
  function initThumbSwipe() {
    document.querySelectorAll(".av-buy__gallery").forEach((gallery) => {
      const main = gallery.querySelector(".av-buy__main");
      const thumbs = Array.from(gallery.querySelectorAll("[data-av-thumb]")).filter((t) => t.dataset.avThumb);
      if (!main || thumbs.length < 2) return;

      let startX = 0, startY = 0, tracking = false, moved = 0;
      const threshold = 45;
      const current = () => {
        const i = thumbs.findIndex((t) => t.classList.contains("is-active"));
        return i < 0 ? 0 : i;
      };
      main.addEventListener("touchstart", (e) => {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX; startY = e.touches[0].clientY;
        tracking = true; moved = 0;
      }, { passive: true });
      main.addEventListener("touchmove", (e) => {
        if (!tracking) return;
        const dx = e.touches[0].clientX - startX;
        const dy = e.touches[0].clientY - startY;
        if (Math.abs(dy) > Math.abs(dx)) { tracking = false; return; }
        moved = dx;
        if (dx < -8) { main.classList.add("is-swiping-left"); main.classList.remove("is-swiping-right"); }
        else if (dx > 8) { main.classList.add("is-swiping-right"); main.classList.remove("is-swiping-left"); }
      }, { passive: true });
      const end = () => {
        main.classList.remove("is-swiping-left", "is-swiping-right");
        if (!tracking) return;
        tracking = false;
        if (Math.abs(moved) < threshold) return;
        const idx = current();
        const next = moved < 0
          ? (idx + 1) % thumbs.length
          : (idx - 1 + thumbs.length) % thumbs.length;
        setActiveThumb(thumbs[next]);
      };
      main.addEventListener("touchend", end, { passive: true });
      main.addEventListener("touchcancel", end, { passive: true });
    });
  }

  /* ----- Sticky mobile buy bar ----- */
  function initStickyBuyBar() {
    const bar = document.querySelector(".av-sticky-buy");
    if (!bar) return;
    const trigger = document.querySelector("[data-sticky-trigger]") || document.querySelector(".av-hero");
    if (!trigger) return;
    if (!("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) bar.classList.remove("is-shown");
        else if (entry.boundingClientRect.top < 0) bar.classList.add("is-shown");
      },
      { threshold: 0 }
    );
    io.observe(trigger);
  }

  /* ----- Spinner keyframes ----- */
  function injectSpinnerKeyframes() {
    if (document.getElementById("av-spin-kf")) return;
    const s = document.createElement("style");
    s.id = "av-spin-kf";
    s.textContent = "@keyframes av-spin{to{transform:rotate(360deg)}}";
    document.head.appendChild(s);
  }

  /* ----- Reset button state after bfcache back nav ----- */
  function initPageShowReset() {
    window.addEventListener("pageshow", () => {
      document.querySelectorAll("[data-av-add-btn]").forEach((btn) => {
        if (btn.classList.contains("is-loading") || /redirect|loading|spin/i.test(btn.innerHTML)) {
          btn.classList.remove("is-loading");
          if (btn.dataset.originalText) btn.innerHTML = btn.dataset.originalText;
        }
      });
    });
  }
})();
